import React, { useState, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { toast } from 'react-toastify';
import { AiOutlineFileImage } from 'react-icons/ai';
import axios from 'axios';

import Modal from '@/components/common/Modal';
import Input from '@/components/common/Input';
import Text from '@/components/common/Text';

import { uploadNotice, modNotice } from '@/apis/notices';
import type { NoticesItem } from '@/types/notices.types';
import type { ErrorResponse } from '@/types/client.types';

import { postPresignedUrlImg } from '@/apis/img';
import { validateAndFixImageFile } from '@/utils/detectImageFormat';
import { useLoadingStore } from '@/store/loading';

import { Theme } from '@/styles/Theme';
import * as S from './style';

interface Props {
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  editingNotice?: NoticesItem | null;
  onSuccess: () => void;
}

const PLATFORM_OPTIONS: { value: 'web' | 'app' | 'common'; label: string }[] = [
  { value: 'web', label: '웹' },
  { value: 'app', label: '앱' },
  { value: 'common', label: '웹+앱' },
];

const NoticeFormModal = ({
  setIsModalOpen,
  editingNotice,
  onSuccess,
}: Props) => {
  const isEdit = !!editingNotice;

  const [platform, setPlatform] = useState<'web' | 'app' | 'common'>('web');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [version, setVersion] = useState('');
  const [isActive, setIsActive] = useState<0 | 1>(1);
  const [important, setImportant] = useState<0 | 1>(0);
  const [isLandingPopup, setIsLandingPopup] = useState<0 | 1>(0);
  const [imageList, setImageList] = useState<string[]>([]);

  const { beginRequest, endRequest, setLoading } = useLoadingStore.getState();

  useEffect(() => {
    if (editingNotice) {
      setPlatform(editingNotice.platform);
      setTitle(editingNotice.title);
      setContent(editingNotice.content);
      setImageList(editingNotice.image ?? []);
      setImportant(editingNotice.important);
      setVersion(editingNotice.version ?? '');
      setIsActive((editingNotice.is_active ?? 1) as 0 | 1);
      setIsLandingPopup((editingNotice.is_landing_popup ?? 0) as 0 | 1);
    }
  }, [editingNotice]);

  const { mutate: uploadMutate } = useMutation({
    mutationFn: uploadNotice,
    onSuccess: () => {
      toast.success('공지사항이 등록되었습니다.');
      onSuccess();
      setIsModalOpen(false);
    },
    onError: (error: AxiosError<ErrorResponse>) => {
      toast.error(error.response?.data?.message ?? '등록에 실패했습니다.');
    },
  });

  const { mutate: modMutate } = useMutation({
    mutationFn: modNotice,
    onSuccess: () => {
      toast.success('공지사항이 수정되었습니다.');
      onSuccess();
      setIsModalOpen(false);
    },
    onError: (error: AxiosError<ErrorResponse>) => {
      toast.error(error.response?.data?.message ?? '수정에 실패했습니다.');
    },
  });

  const handleImgChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      beginRequest();
      if (e.target.files && e.target.files.length > 0) {
        const files = e.target.files;
        const uploadedUrls: string[] = [];

        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          const validationResult = await validateAndFixImageFile(file);

          if (!validationResult.isValid) {
            toast.error(
              validationResult.error || '이미지 파일만 업로드 가능합니다.'
            );
            continue;
          }

          const { data } = await postPresignedUrlImg({
            files: [validationResult.fixedName],
          });
          const url = data[0];
          await axios.put(url, file, { timeout: 120000 });
          uploadedUrls.push(url.split('?')[0]);
        }

        setImageList((prev) => [...prev, ...uploadedUrls]);
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError<ErrorResponse>;
        toast.error(axiosError.response?.data?.message ?? '이미지 업로드 실패');
      } else {
        toast.error('이미지 업로드 중 오류가 발생했습니다.');
      }
    } finally {
      endRequest();
      setLoading(false);
      e.target.value = '';
    }
  };

  const handleRemoveImage = (index: number) => {
    setImageList((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = () => {
    if (!title.trim()) {
      toast.error('제목을 입력해주세요.');
      return;
    }
    if (!content.trim()) {
      toast.error('내용을 입력해주세요.');
      return;
    }

    const payload = {
      platform,
      title: title.trim(),
      content: content.trim(),
      version: version.trim() || undefined,
      isActive,
      image: imageList,
      important,
      isLandingPopup,
    };

    if (isEdit && editingNotice) {
      modMutate({ ...payload, id: editingNotice.id });
    } else {
      uploadMutate(payload);
    }
  };

  return (
    <Modal
      width='43.75rem'
      closeText='취소'
      confirmText={isEdit ? '수정' : '등록'}
      setIsModalOpen={setIsModalOpen}
      hasButton={true}
      onClickConfirm={handleSubmit}
    >
      <S.Container>
        <S.FormSection>
          <S.Label>플랫폼</S.Label>
          <S.Select
            value={platform}
            onChange={(e) =>
              setPlatform(e.target.value as 'web' | 'app' | 'common')
            }
          >
            {PLATFORM_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </S.Select>
        </S.FormSection>

        <S.FormSection>
          <S.Label>제목 *</S.Label>
          <Input
            name='title'
            width='100%'
            height='2.5rem'
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder='공지 제목을 입력하세요'
          />
        </S.FormSection>

        <S.FormSection>
          <S.Label>내용 *</S.Label>
          <S.Textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder='공지 내용을 입력하세요'
            rows={8}
          />
        </S.FormSection>

        <S.FormSection>
          <S.Label>버전</S.Label>
          <Input
            name='version'
            width='100%'
            height='2.5rem'
            value={version}
            onChange={(e) => setVersion(e.target.value)}
            placeholder='예: v2.4.0'
          />
        </S.FormSection>

        <S.FormSection>
          <S.Label>팝업 표시</S.Label>
          <S.OptionRow>
            <S.StyledCheckbox
              type='checkbox'
              id='notice_popup'
              checked={isActive === 1}
              onChange={(e) => setIsActive(e.target.checked ? 1 : 0)}
            />
            <S.CheckboxLabel htmlFor='notice_popup'>
              로그인 시 팝업으로 표시
            </S.CheckboxLabel>
          </S.OptionRow>
        </S.FormSection>

        <S.FormSection>
          <S.Label>중요 공지</S.Label>
          <S.OptionRow>
            <S.StyledCheckbox
              type='checkbox'
              id='notice_important'
              checked={important === 1}
              onChange={(e) => setImportant(e.target.checked ? 1 : 0)}
            />
            <S.CheckboxLabel htmlFor='notice_important'>
              중요 공지로 표시
            </S.CheckboxLabel>
          </S.OptionRow>
        </S.FormSection>

        <S.FormSection>
          <S.Label>랜딩 팝업</S.Label>
          <S.OptionRow>
            <S.StyledCheckbox
              type='checkbox'
              id='notice_landing_popup'
              checked={isLandingPopup === 1}
              onChange={(e) => setIsLandingPopup(e.target.checked ? 1 : 0)}
            />
            <S.CheckboxLabel htmlFor='notice_landing_popup'>
              로그인 전 랜딩 페이지 팝업으로 표시
            </S.CheckboxLabel>
          </S.OptionRow>
          <S.HelperText>
            랜딩 팝업은 하나만 표시됩니다. 선택 시 기존 랜딩 팝업이 대체됩니다.
          </S.HelperText>
        </S.FormSection>

        <S.FormSection>
          <S.Label>이미지</S.Label>
          <S.ImageUploadArea>
            <S.FileInput
              type='file'
              id='notice_img_upload'
              accept='.jpg,.jpeg,.png,.webp,.gif'
              multiple
              onChange={handleImgChange}
            />
            <S.FileLabel htmlFor='notice_img_upload'>
              <AiOutlineFileImage size={32} color={Theme.colors.G_2} />
              <Text color={Theme.colors.G_2} fontSize='0.875rem'>
                이미지 추가 (여러 장 선택 가능)
              </Text>
            </S.FileLabel>
          </S.ImageUploadArea>
          {imageList.length > 0 && (
            <S.ImageList>
              {imageList.map((url, idx) => (
                <S.ImageItem key={idx}>
                  <S.ImagePreview src={url} alt={`notice_${idx}`} />
                  <S.RemoveImgBtn onClick={() => handleRemoveImage(idx)}>
                    삭제
                  </S.RemoveImgBtn>
                </S.ImageItem>
              ))}
            </S.ImageList>
          )}
        </S.FormSection>
      </S.Container>
    </Modal>
  );
};

export default NoticeFormModal;
